import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { CheckCircle, Email, Login, Refresh, WarningAmber, WhatsApp } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrandLogo from '../../common/BrandLogo';
import { useAuth } from '../AuthContext';
import { verifyProfessionalEmailToken } from '../../../services/integrations/backend';
import { friendlyError } from '../../../utils/userMessages';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const started = useRef(false);
  const {
    currentUser,
    userData,
    publicRuntime,
    checkEmailVerification,
    sendVerificationEmail,
    verifyAccountWithWhatsAppOtp,
  } = useAuth();
  const provider = publicRuntime?.auth?.verificationProvider || 'firebase_auth';
  const [state, setState] = useState({ status: 'loading', message: 'Checking your account verification…' });
  const [phone, setPhone] = useState(userData?.phone || '');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (userData?.phone && !phone) setPhone(userData.phone); }, [userData?.phone, phone]);

  useEffect(() => {
    if (started.current || !publicRuntime) return;
    started.current = true;
    const token = params.get('token') || '';

    const run = async () => {
      try {
        if (token) {
          const result = await verifyProfessionalEmailToken(token);
          setState({ status: 'success', message: `${result?.email || 'Your account'} has been verified. You can continue to TELCOFAIR.` });
          return;
        }
        if (provider === 'firebase_auth') {
          const result = await checkEmailVerification(currentUser?.uid);
          setState(result?.verified
            ? { status: 'success', message: 'Your email address is verified.' }
            : { status: 'pending', message: 'Open the Firebase verification email, complete verification, then check again.' });
          return;
        }
        if (provider === 'whatsapp') {
          setState({ status: 'pending', message: 'Verify this account using the WhatsApp OTP sent to your registered number.' });
          return;
        }
        if (provider === 'manual') {
          setState({ status: 'pending', message: 'Your account is awaiting manual verification by TELCOFAIR Super Admin.' });
          return;
        }
        setState({ status: 'pending', message: 'Open the professional verification email sent by TELCOFAIR and use its verification link.' });
      } catch (error) {
        setState({ status: 'error', message: friendlyError(error, 'This verification request is invalid, expired, or could not be completed.') });
      }
    };
    run();
  }, [publicRuntime, provider, params, currentUser?.uid, checkEmailVerification]);

  const resend = async () => {
    if (!currentUser?.uid) {
      setState({ status: 'error', message: 'Sign in to your incomplete account first, then request a new verification message.' });
      return;
    }
    setBusy(true);
    try {
      const result = await sendVerificationEmail(
        currentUser.email || userData?.email || '',
        userData?.firstName || '',
        userData?.lastName || '',
        currentUser.uid
      );
      if (!result?.success) throw new Error(result?.error || 'Verification delivery failed.');
      setState({ status: 'pending', message: provider === 'whatsapp' ? 'A new WhatsApp OTP was requested.' : provider === 'manual' ? 'Your account is still awaiting Super Admin verification.' : 'A new verification message was sent.' });
    } catch (error) {
      setState({ status: 'error', message: friendlyError(error, 'Unable to send a new verification message.') });
    } finally { setBusy(false); }
  };

  const refreshFirebase = async () => {
    setBusy(true);
    try {
      const result = await checkEmailVerification(currentUser?.uid);
      setState(result?.verified
        ? { status: 'success', message: 'Your email address is verified.' }
        : { status: 'pending', message: 'Verification is still pending. Complete the verification email and check again.' });
    } finally { setBusy(false); }
  };

  const verifyWhatsApp = async () => {
    if (!phone.trim() || !otp.trim()) {
      setState({ status: 'error', message: 'Enter your registered WhatsApp number and verification code.' });
      return;
    }
    setBusy(true);
    try {
      await verifyAccountWithWhatsAppOtp(phone.trim(), otp.trim());
      setState({ status: 'success', message: 'Your TELCOFAIR account has been verified through WhatsApp.' });
    } catch (error) {
      setState({ status: 'error', message: friendlyError(error, 'The WhatsApp verification code is invalid or expired.') });
    } finally { setBusy(false); }
  };

  const success = state.status === 'success';
  const failed = state.status === 'error';
  const pending = state.status === 'pending';

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: '#F2F7F9', backgroundImage: 'radial-gradient(circle at 10% 12%, rgba(46,111,142,.12), transparent 28%), radial-gradient(circle at 88% 82%, rgba(66,184,155,.12), transparent 30%)' }}>
      <Paper elevation={0} sx={{ width: 'min(540px, 100%)', p: { xs: 3, md: 4.5 }, borderRadius: '22px', border: '1px solid rgba(31,62,82,.10)', boxShadow: '0 28px 80px -38px rgba(31,62,82,.38)', textAlign: 'center', overflow: 'hidden', position: 'relative', '&::before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 4, background: 'linear-gradient(90deg,#2E6F8E,#42B89B,#7C5CF2)' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.2 }}><BrandLogo height={52} /></Box>
        <Box sx={{ width: 72, height: 72, borderRadius: '20px', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2.5, bgcolor: success ? '#E3F3EC' : failed ? '#FBE7E6' : '#E4F0F6', color: success ? '#2E8B69' : failed ? '#BC3A36' : '#2E6F8E' }}>
          {state.status === 'loading' ? <CircularProgress size={30} sx={{ color: '#2E6F8E' }} /> : success ? <CheckCircle sx={{ fontSize: 38 }} /> : provider === 'whatsapp' ? <WhatsApp sx={{ fontSize: 38 }} /> : failed ? <WarningAmber sx={{ fontSize: 38 }} /> : <Email sx={{ fontSize: 38 }} />}
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1.45rem', color: '#182235' }}>{success ? 'Account verified' : failed ? 'Verification problem' : 'Account verification'}</Typography>
        <Typography sx={{ mt: 1, color: '#4F6175', lineHeight: 1.65, fontSize: '.92rem' }}>{state.message}</Typography>

        {pending && provider === 'whatsapp' && (
          <Box sx={{ mt: 2.5, display: 'grid', gap: 1.2, textAlign: 'left' }}>
            <TextField label="Registered WhatsApp number" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" fullWidth />
            <TextField label="WhatsApp OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))} size="small" fullWidth />
            <Button onClick={verifyWhatsApp} disabled={busy} variant="contained" startIcon={<WhatsApp />} sx={{ borderRadius: '11px', textTransform: 'none', fontWeight: 800, color: '#fff', bgcolor: '#2E8B69' }}>Verify WhatsApp OTP</Button>
            <Button onClick={resend} disabled={busy} sx={{ textTransform: 'none', fontWeight: 750 }}>Send / resend code</Button>
          </Box>
        )}

        {pending && provider === 'firebase_auth' && (
          <Box sx={{ mt: 2.5, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={refreshFirebase} disabled={busy} startIcon={<Refresh />} variant="contained" sx={{ borderRadius: '11px', textTransform: 'none', fontWeight: 800 }}>Check verification</Button>
            <Button onClick={resend} disabled={busy} sx={{ textTransform: 'none', fontWeight: 750 }}>Resend email</Button>
          </Box>
        )}

        {pending && provider === 'professional_email' && currentUser && (
          <Button onClick={resend} disabled={busy} sx={{ mt: 2.5, textTransform: 'none', fontWeight: 800 }}>Resend professional verification email</Button>
        )}

        {failed && <Alert severity="warning" sx={{ mt: 2.2, textAlign: 'left', borderRadius: '12px' }}>You can request a new verification message or contact TELCOFAIR support if the problem continues.</Alert>}

        {state.status !== 'loading' && (
          <Button variant={success ? 'contained' : 'outlined'} startIcon={<Login />} onClick={() => navigate('/login', { replace: true })} sx={{ mt: 3, borderRadius: '11px', px: 3, py: 1.1, textTransform: 'none', fontWeight: 800, ...(success ? { color: '#fff', background: 'linear-gradient(135deg,#2E6F8E,#42B89B)' } : {}) }}>Go to login</Button>
        )}
      </Paper>
    </Box>
  );
}
